import { useIntl } from '@umijs/max';
import { Select, type SelectProps } from 'antd';
import React, { useCallback, useEffect, useState } from 'react';
import { listTagLexiconsCached } from '@/services/admin/dictionary';
import { getDisplayTextByLocale } from '@/utils/locale';

export interface BenefitTagSelectProps
  extends Omit<
    SelectProps<string[]>,
    'options' | 'mode' | 'loading' | 'onSearch' | 'filterOption'
  > {
  /**
   * 词库分类；不传或传 `all` 时加载全部四类下已启用项（岗位表单默认）。
   * 传具体枚举值时仅加载该分类（兼容旧用法）。
   */
  category?: string;
}

/**
 * 岗位标签词库多选（dict_tag_lexicons，存 code；默认拉取各分类已启用项）
 */
const BenefitTagSelect: React.FC<BenefitTagSelectProps> = ({
  category = 'all',
  placeholder,
  value,
  onChange,
  disabled,
  className,
  style,
  allowClear = true,
  ...rest
}) => {
  const intl = useIntl();
  const [loading, setLoading] = useState(true);
  const [options, setOptions] = useState<
    Array<{ label: string; value: string; disabled?: boolean }>
  >([]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listTagLexiconsCached({
        category: category === 'all' ? 'all' : category,
        is_active: true,
      });
      setOptions(
        data.map((row) => {
          const catLabel = intl.formatMessage({
            id: `页面.配置.福利与保障标签.分类.${row.category}`,
            defaultMessage: row.category,
          });
          const text = getDisplayTextByLocale({
            zh_CN: row.label_zh,
            en_US: row.label_en,
            km_KH: row.label_km,
          });
          return {
            value: row.code,
            label: `${catLabel} · ${text}`,
            disabled: row.is_active === false,
          };
        }),
      );
    } catch {
      setOptions([]);
    } finally {
      setLoading(false);
    }
  }, [category, intl]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <Select<string[]>
      mode="multiple"
      allowClear={allowClear}
      className={className}
      style={style}
      disabled={disabled}
      placeholder={placeholder}
      loading={loading}
      value={value}
      onChange={onChange}
      options={options}
      showSearch
      optionFilterProp="label"
      maxTagCount="responsive"
      {...rest}
    />
  );
};

export default BenefitTagSelect;
